import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import { ManageableProduct } from '../products';
import {
  initManageProductsState,
  manageProductsReducer,
  ManageProductsState,
  ProductDefaults,
  ProductEntry,
  ProductsDisplaySettings,
} from './manageProducts.reducer';

interface ManageProductsContextProps {
  selectedProducts: ManageableProduct[];
  productDefaults: ProductDefaults;
  displaySettings: ProductsDisplaySettings;
  updateProduct: (productId: string, updates: Partial<ProductEntry>) => void;
  setProducts: (products: ProductEntry[]) => void;
  setProductDefaults: (product: ProductDefaults) => void;
  setDisplaySettings: (
    displaySettings: Partial<ProductsDisplaySettings>
  ) => void;
}

const ManageProductsContext = createContext<
  ManageProductsContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<ManageProductsState>;
}>;

export const ManageProductsProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(manageProductsReducer, {
    ...initManageProductsState,
    ...initialState,
  });

  const selectedProducts = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .map((product) => ({
          ...product,
          ...state.productDefaults,
        })),
    [state.entities, state.ids, state.productDefaults]
  );

  const updateProduct: ManageProductsContextProps['updateProduct'] =
    useCallback(
      (productId, updates) =>
        dispatch({
          type: 'UPDATE_PRODUCT',
          payload: { productId, updates },
        }),
      []
    );

  const setProducts: ManageProductsContextProps['setProducts'] = useCallback(
    (products) =>
      dispatch({
        type: 'SET_PRODUCTS',
        payload: { products },
      }),
    []
  );

  const setProductDefaults: ManageProductsContextProps['setProductDefaults'] =
    useCallback(
      (product) =>
        dispatch({
          type: 'SET_PRODUCT_DEFAULTS',
          payload: { product },
        }),
      []
    );

  const setDisplaySettings: ManageProductsContextProps['setDisplaySettings'] =
    useCallback((displaySettings) => {
      dispatch({
        type: 'SET_DISPLAY_SETTINGS',
        payload: { displaySettings },
      });
    }, []);

  return (
    <ManageProductsContext.Provider
      value={{
        selectedProducts,
        displaySettings: state.displaySettings,
        productDefaults: state.productDefaults,
        updateProduct,
        setProducts,
        setProductDefaults,
        setDisplaySettings,
      }}
    >
      {children}
    </ManageProductsContext.Provider>
  );
};

export const useManageProducts = () => {
  const context = useContext(ManageProductsContext);
  if (context === undefined) {
    throw new Error(
      'useManageProducts must be used within a ManageProductsProvider'
    );
  }
  return context;
};
